// Frontend/src/components/admin/forms/EventForm.jsx
import { useEffect, useState } from 'react';
import { STORAGE_PATHS } from '../../../constants/storagePaths';
import { uploadFile } from '../../../services/uploadFile';
import Checkbox from '../ui/Checkbox';
import CustomDatePicker from '../ui/DatePicker';
import FileUpload from '../ui/FileUpload';
import Input from '../ui/Input';
import Select from '../ui/Select';

const EventForm = ({ initialValues, onSubmit, onCancel, campuses = [], ministries = [] }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    isSpanningEvent: false,
    location: '',
    address: '',
    imageUrl: '',
    eventType: 'service',
    status: 'upcoming',
    registrationRequired: false,
    registrationDeadline: null,
    cost: 0,
    contactEmail: '',
    contactPhone: '',
    isRecurring: false,
    recurrencePattern: '',
    campusId: '',
    ministry_name: '',
    ...initialValues,
  });

  const [imagePreview, setImagePreview] = useState(initialValues?.imageUrl || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [errors, setErrors] = useState({});

  const parseDate = (dateValue) => {
  if (!dateValue) return new Date();
  
  // If already a Date object and valid, return as-is
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return dateValue;
  }
  
  // Handle Firestore Timestamp format
  if (dateValue._seconds && typeof dateValue._seconds === 'number') {
    const milliseconds = dateValue._seconds * 1000 + Math.floor(dateValue._nanoseconds / 1000000);
    return new Date(milliseconds);
  }
  
  // Handle ISO string or other date formats
  const parsed = new Date(dateValue);
  if (isNaN(parsed.getTime())) {
    console.warn('Invalid date value:', dateValue, 'Using current date instead');
    return new Date();
  }
  return parsed;
};

   useEffect(() => {
      if (initialValues) {
        const parsedInitialValues = {
          ...initialValues,
          startDate: parseDate(initialValues.startDate || initialValues.date),
          endDate: parseDate(initialValues.endDate || initialValues.date),
          registrationDeadline: initialValues.registrationDeadline 
            ? parseDate(initialValues.registrationDeadline) 
            : null
        };
  
        setFormData(prev => ({
          ...prev,
          ...parsedInitialValues
        }));
        
        setImagePreview(initialValues.imageUrl || '');
        setShowRecurrence(initialValues.isRecurring || false);
        setShowRegistration(initialValues.registrationRequired || false);
      }
    }, [initialValues]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = async (file) => {
    try {
      const imageUrl = await uploadFile(file, STORAGE_PATHS.EVENTS);
      setFormData((prev) => ({ ...prev, imageUrl }));
      setImagePreview(URL.createObjectURL(file));
    } catch (error) {
      console.error('Failed to upload image:', error);
      setErrors((prev) => ({ ...prev, imageUrl: 'Failed to upload image' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Event title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.eventType) newErrors.eventType = 'Event type is required';
    if (!formData.startDate || isNaN(formData.startDate.getTime())) {
      newErrors.startDate = 'Valid start date is required';
    }
    if (formData.isSpanningEvent && (!formData.endDate || isNaN(formData.endDate.getTime()))) {
      newErrors.endDate = 'Valid end date is required for multi-day events';
    }
    if (formData.registrationRequired && (!formData.registrationDeadline || isNaN(formData.registrationDeadline.getTime()))) {
      newErrors.registrationDeadline = 'Valid registration deadline is required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const submissionData = {
        ...formData,
        startDate: formData.startDate instanceof Date && !isNaN(formData.startDate.getTime())
          ? formData.startDate.toISOString()
          : new Date().toISOString(),
        endDate: formData.isSpanningEvent && formData.endDate instanceof Date && !isNaN(formData.endDate.getTime())
          ? formData.endDate.toISOString()
          : formData.startDate.toISOString(),
        registrationDeadline: formData.registrationDeadline instanceof Date && !isNaN(formData.registrationDeadline.getTime())
          ? formData.registrationDeadline.toISOString() 
          : null
      };
      await onSubmit(submissionData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors((prev) => ({ ...prev, submit: error.message || 'Failed to save event' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
          {errors.submit}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Event Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          error={errors.title}
        />
        <Select
          label="Event Type"
          name="eventType"
          value={formData.eventType}
          onChange={handleChange}
          options={[
            { value: 'service', label: 'Service' },
            { value: 'conference', label: 'Conference' },
            { value: 'outreach', label: 'Crusade' },
            { value: 'meeting', label: 'Meeting' },
            { value: 'social', label: 'Social' },
          ]}
          required
          error={errors.eventType}
        />
      </div>

      <Input
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleChange}
        type="textarea"
        required
        error={errors.description}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <CustomDatePicker
          label="Start Date & Time"
          selected={formData.startDate}
          onChange={(date) => {
            setFormData((prev) => ({ ...prev, startDate: date }));
            setErrors((prev) => ({ ...prev, startDate: '' }));
          }}
          showTimeSelect
          required
          error={errors.startDate}
        />
        <div className="space-y-2">
          <Checkbox
            label="Multi-day Event"
            name="isSpanningEvent"
            checked={formData.isSpanningEvent}
            onChange={(e) => {
              handleChange(e);
              if (!e.target.checked) {
                setFormData((prev) => ({ ...prev, endDate: prev.startDate }));
                setErrors((prev) => ({ ...prev, endDate: '' }));
              }
            }}
          />
          {formData.isSpanningEvent && (
            <CustomDatePicker
              label="End Date & Time"
              selected={formData.endDate}
              onChange={(date) => {
                setFormData((prev) => ({ ...prev, endDate: date }));
                setErrors((prev) => ({ ...prev, endDate: '' }));
              }}
              showTimeSelect
              minDate={formData.startDate}
              required
              error={errors.endDate}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Event Status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          options={[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'ongoing', label: 'Ongoing' },
            { value: 'past', label: 'Past' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Location Name"
          name="location"
          value={formData.location}
          onChange={handleChange}
          required
          error={errors.location}
        />
        <Input
          label="Full Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Campus"
          name="campusId"
          value={formData.campusId || ''}
          onChange={handleChange}
          options={[{ value: '', label: 'Select a campus', disabled: true }, ...campuses]}
        />
        <Select
          label="Ministry"
          name="ministry_name"
          value={formData.ministry_name || ''}
          onChange={handleChange}
          options={[{ value: '', label: 'Select a ministry', disabled: true }, ...ministries]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Contact Email"
          name="contactEmail"
          type="email"
          value={formData.contactEmail}
          onChange={handleChange}
        />
        <Input
          label="Contact Phone"
          name="contactPhone"
          value={formData.contactPhone}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Checkbox
            label="Registration Required"
            name="registrationRequired"
            checked={formData.registrationRequired}
            onChange={(e) => {
              handleChange(e);
              setShowRegistration(e.target.checked);
              if (!e.target.checked) {
                setFormData((prev) => ({ ...prev, registrationDeadline: null, cost: 0 }));
                setErrors((prev) => ({ ...prev, registrationDeadline: '' }));
              }
            }}
          />
          {showRegistration && (
            <>
              <CustomDatePicker
                label="Registration Deadline"
                selected={formData.registrationDeadline}
                onChange={(date) => {
                  setFormData((prev) => ({ ...prev, registrationDeadline: date }));
                  setErrors((prev) => ({ ...prev, registrationDeadline: '' }));
                }}
                showTimeSelect
                error={errors.registrationDeadline}
              />
              <Input
                label="Cost (in dollars)"
                name="cost"
                type="number"
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={handleChange}
              />
            </>
          )}
        </div>
        <div className="space-y-2">
          <Checkbox
            label="Recurring Event"
            name="isRecurring"
            checked={formData.isRecurring}
            onChange={(e) => {
              handleChange(e);
              setShowRecurrence(e.target.checked);
              if (!e.target.checked) {
                setFormData((prev) => ({ ...prev, recurrencePattern: '' }));
              }
            }}
          />
          {showRecurrence && (
            <Select
              label="Recurrence Pattern"
              name="recurrencePattern"
              value={formData.recurrencePattern}
              onChange={handleChange}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' },
              ]}
            />
          )}
        </div>
      </div>

      <FileUpload
        label="Featured Image"
        onFileChange={handleFileChange}
        preview={imagePreview}
        error={errors.imageUrl}
      />

      <div className="flex justify-end space-x-3 pt-4">
        <button type="button" className="btn btn-outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Event'}
        </button>
      </div>
    </form>
  );
};

export default EventForm;